import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';

interface UserTenant {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  role: string;
  tenantUserId: string;
}

interface PlatformUser {
  id: string;
  email: string;
  fullName: string;
  externalAuthId?: string;
  createdAt: string;
  tenants?: UserTenant[];
  tenantsCount?: number;
}

import { useTheme } from '@/hooks/useTheme';

export default function PlatformUsersPage() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [items, setItems] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await api.get<PlatformUser[]>('/admin/users');
      setItems(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setItems([]);
      } else {
        addToast(getApiErrorMessage(err, t('adm_err_users')), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const tableBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const separator = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const hoverBg = isDark ? 'rgba(255,255,255,0.04)' : '#F9F9FB';
  const iconBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const selectBg = isDark ? '#1A1A1A' : '#fff';
  const expandedBg = isDark ? 'rgba(255,255,255,0.02)' : '#FAFAFA';

  const filtered = items.filter(
    (u) =>
      (u.fullName ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (u.email ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (u.externalAuthId ?? '').toLowerCase().includes(search.toLowerCase()),
  );

  const getRoleBadgeStyle = (role: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      owner: { bg: 'var(--surface-2)', color: 'var(--text)' },
      admin: { bg: 'rgba(184,134,11,0.1)', color: '#B8860B' },
      editor: { bg: 'rgba(31,91,143,0.1)', color: '#1F5B8F' },
      operator: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      finance: { bg: 'rgba(107,107,112,0.15)', color: '#4A4A50' },
      viewer: { bg: 'rgba(107,107,112,0.08)', color: '#6B6B70' },
    };
    return map[role] ?? map.viewer;
  };

  return (
    <DashboardLayout breadcrumb={[t('pf_section'), t('pf_users_overview')]}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
            Users Overview
          </h1>
          <p className="text-sm mt-1" style={{ color: textMuted }}>
            All platform users and their tenant memberships
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl" style={{ border: '1px solid #E5E5E8' }}>
        <div className="flex items-center gap-3 p-4" style={{ borderBottom: '1px solid #E5E5E8' }}>
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-md flex-1 max-w-md"
            style={{ background: '#F5F5F7', border: '1px solid #E5E5E8' }}
          >
            <i className="ri-search-line text-sm" style={{ color: '#6B6B70' }}></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email or auth ID..."
              className="bg-transparent outline-none text-sm flex-1"
              style={{ color: '#1A1A1A' }}
            />
          </div>
          <button
            onClick={loadUsers}
            className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors"
            style={{ border: '1px solid #E5E5E8' }}
            title="Refresh"
          >
            <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`} style={{ color: '#6B6B70' }}></i>
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: '#6B6B70' }}></i>
            <p className="text-sm mt-3" style={{ color: '#6B6B70' }}>Loading users...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: '#F5F5F7' }}>
              <i className="ri-user-line text-xl" style={{ color: '#6B6B70' }}></i>
            </div>
            <p className="text-sm font-medium" style={{ color: '#1A1A1A' }}>{t('adm_no_users')}</p>
            <p className="text-xs mt-1" style={{ color: '#6B6B70' }}>
              {search ? t('adm_try_search') : 'No users registered in the system'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#F5F5F7' }}>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>{t('common_user')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Email</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>{t('pf_auth_id')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>{t('common_created')}</th>
                  <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>{t('adm_tenants')}</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((user) => {
                  const isExpanded = expandedId === user.id;
                  return (
                    <>
                      <tr
                        key={user.id}
                        className="cursor-pointer transition-colors hover:bg-gray-50"
                        onClick={() => setExpandedId(isExpanded ? null : user.id)}
                        style={{ borderBottom: '1px solid #F5F5F7' }}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                              style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', color: 'var(--text)' }}
                            >
                              {(user.fullName || '?').slice(0, 1).toUpperCase()}
                            </div>
                            <span className="font-medium" style={{ color: '#1A1A1A' }}>{user.fullName}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#6B6B70' }}>{user.email}</td>
                        <td className="px-4 py-3 font-mono text-xs" style={{ color: '#6B6B70' }}>
                          {user.externalAuthId || '—'}
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#6B6B70' }}>
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString('uk-UA') : '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-medium" style={{ color: '#1A1A1A' }}>
                          {user.tenantsCount ?? (user.tenants?.length ?? 0)}
                        </td>
                        <td className="px-4 py-3">
                          <button className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer hover:bg-gray-100 transition-colors">
                            <i className={`ri-arrow-down-s-line transition-transform ${isExpanded ? 'rotate-180' : ''}`} style={{ color: '#6B6B70' }}></i>
                          </button>
                        </td>
                      </tr>
                      {isExpanded && user.tenants && user.tenants.length > 0 && (
                        <tr>
                          <td colSpan={6} className="px-0 py-0">
                            <div className="px-4 py-4" style={{ background: '#FAFAFA' }}>
                              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>
                                Tenant Memberships
                              </span>
                              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {user.tenants.map((t) => {
                                  const roleStyle = getRoleBadgeStyle(t.role);
                                  return (
                                    <div
                                      key={t.tenantUserId}
                                      className="bg-white rounded-lg p-3"
                                      style={{ border: '1px solid #E5E5E8' }}
                                    >
                                      <div className="text-sm font-medium" style={{ color: '#1A1A1A' }}>{t.tenantName}</div>
                                      <div className="text-xs font-mono mt-0.5" style={{ color: '#6B6B70' }}>{t.tenantSlug}</div>
                                      <div className="flex items-center justify-between mt-2">
                                        <span
                                          className="text-[11px] font-medium px-2 py-0.5 rounded-full capitalize"
                                          style={{ background: roleStyle.bg, color: roleStyle.color }}
                                        >
                                          {t.role}
                                        </span>
                                        <span className="text-[10px] font-mono" style={{ color: '#6B6B70' }}>
                                          {t.tenantUserId}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}